import {defer, map, tap} from 'rxjs';
import {web3Api} from '@/api/web3';
import {doCaptcha} from '@/captcha';
import {defineObservableSteps} from '@/streams/utils';
import type {SupportConnectors} from '@/streams/wallet-connect';
import {createGetWalletObservable, createGetWalletSignMesageObservable} from '@/streams/wallet-connect/utils';

/**
 * 创建钱包登录注册步骤
 */
export function createAccessWithWaletSteps(params: Params) {
  const {LG, connector, referralCode = '', group = ''} = params;

  let walletAddressData;
  let signMessageData;
  let googleCaptchaData;

  return defineObservableSteps([
    // 连接钱包
    {
      name: 'walletConnect',
      generateObservable() {
        return createGetWalletObservable(connector).pipe(
          tap((wallet) => {
            walletAddressData = wallet;
          })
        );
      },
    },
    // 签名
    {
      name: 'signMessage',
      generateObservable() {
        const notificationType = 'WEB3_WALLET_LOGIN_SIGNATURE';
        return createGetWalletSignMesageObservable({notificationType, address: walletAddressData.address}).pipe(
          tap((signedResult) => {
            signMessageData = signedResult;
          })
        );
      },
    },
    // 谷歌行为认证
    {
      name: 'googleCaptcha',
      generateObservable(context) {
        return defer(() => doCaptcha('walletLogin')).pipe(
          tap((response) => {
            if (!response) {
              throw new Error(LG(langMaps.NetworkError));
            }
            googleCaptchaData = response;
          })
        );
      },
    },
    // 钱包登录
    {
      name: 'walletLogin',
      generateObservable(params, context) {
        return defer(() =>
          web3Api.walletLogin({
            query: {
              recaptchaToken: googleCaptchaData.recaptchaToken,
              recaptchaVersion: googleCaptchaData.recaptchaVersion,
            },
            params: {
              generalStartCode: signMessageData.generalStartCode,
              signedMessage: signMessageData.signedMessage,
              address: walletAddressData.address,
              walletName: walletAddressData.walletType,
              walletSource: walletAddressData.connectorType,
              referralCode,
              group,
              userDeviceLang: (navigator.language || navigator.userLanguage).substring(0, 2),
            },
          })
        ).pipe(
          tap((response) => {
            const error = response?.error;
            if (error) {
              throw new Error(error.message);
            }
          }),
          map(() => ({address: walletAddressData.address}))
        );
      },
    },
  ]);
}

const langMaps = {
  NetworkError: {
    en: 'Network error',
    cn: '网络错误',
    ru: 'Ошибка сети, пожалуйста',
    ko: '인터넷 오류',
    ja: 'ネットワークエラーです',
    es: 'Error de red',
    fr: 'Erreur réseau',
    de: 'Netzwerkfehler',
    tr: 'Ağ hatası',
    pt: 'Erro de rede',
    vi: 'Lỗi mạng',
  },
};

interface Params {
  connector: SupportConnectors;
  LG: any;
  referralCode?: string | number;
  group?: string | number;
}
